import { useCallback, useRef } from 'react'

const STORAGE_KEY = 'optimus_session_start_times'

/**
 * Gerencia o timestamp de início de processamento por sessão,
 * persistindo em localStorage para sobreviver a refresh de página.
 *
 * Extraído de AgentChat.tsx para permitir testes isolados e reuso.
 */
export function useSessionStartTimes() {
  const startTimes = useRef<Map<string, number>>(new Map())
  const initialized = useRef(false)

  if (!initialized.current) {
    initialized.current = true
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, number>
        Object.entries(parsed).forEach(([k, v]) => startTimes.current.set(k, v))
      }
    } catch {
      // ignore parse errors
    }
  }

  const persist = useCallback(() => {
    try {
      const obj: Record<string, number> = {}
      startTimes.current.forEach((v, k) => {
        obj[k] = v
      })
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj))
    } catch {
      // ignore storage errors
    }
  }, [])

  const set = useCallback(
    (sessionId: string, startTime: number) => {
      startTimes.current.set(sessionId, startTime)
      persist()
    },
    [persist]
  )

  const remove = useCallback(
    (sessionId: string) => {
      startTimes.current.delete(sessionId)
      persist()
    },
    [persist]
  )

  const get = useCallback(
    (sessionId: string) => startTimes.current.get(sessionId),
    []
  )

  return { ref: startTimes, set, remove, get }
}
